import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import { MainTable } from "components";
import { IMainTableColumn } from "components/MainTable/MainTableInterfaces";
import { ThirdPartyApiAccessEntity } from "entities/ThirdPartyApiAccess/ThirdPartyApiAccessEntity";
import { useStyles } from "./ApiAccessOversight.styles";
import { IApiAccessCorporation } from "./ApiAccessOversightInterfaces";
import useApiAccessToggle from "./hooks/useApiAccessToggle";
import useApiAccessColumns from "./hooks/useApiAccessColumns";
import { ApiAccessKeysModal } from "./ApiAccessKeysModal.view";

/**
 * MBO "API Access" oversight page (OS-2551). Standard Hasura/JWT back-office surface
 * under the osorno_administrator role, mirroring the OEE module-license flow. Lists ALL
 * Corporations with their enable-gate state, active-key count and most-recent usage;
 * staff can enable/disable any from here (no confirm modal on OFF — helper text warns),
 * drill into per-key metadata, and revoke a single key (confirm modal) for incidents.
 */
export const ApiAccessOversight = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [search, setSearch] = useState<string>("");
    const [selectedCorporation, setSelectedCorporation] = useState<IApiAccessCorporation | undefined>(undefined);

    const { loading, corporations, apolloQueryError, refetch } = ThirdPartyApiAccessEntity.GetApiAccessCorporations();
    const { getApiAccessStatus, enableApiAccess, disableApiAccess, toggling } = useApiAccessToggle();

    const handleToggle = useCallback(
        async (row: IApiAccessCorporation, enable: boolean) => {
            if (enable) {
                await enableApiAccess(row);
            } else {
                // No confirm modal on OFF (contract §10.3 / UX Flow H): the DB trigger
                // auto-revokes all of this Corporation's active keys in the same txn.
                await disableApiAccess(row);
            }
            await refetch();
        },
        [enableApiAccess, disableApiAccess, refetch],
    );

    const handleViewKeys = useCallback((row: IApiAccessCorporation) => setSelectedCorporation(row), []);

    const columns = useApiAccessColumns(
        getApiAccessStatus,
        handleToggle,
        handleViewKeys,
        toggling,
    ) as unknown as IMainTableColumn[];

    const filteredCorporations = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) {
            return corporations;
        }
        return corporations.filter((corporation) => corporation.name.toLowerCase().includes(term));
    }, [corporations, search]);

    return (
        <div className={classes.pageWrapper}>
            <div className={classes.headerRow}>
                <h2 className={classes.title}>{t("api_access.title")}</h2>
                <TextField
                    className={classes.search}
                    size="small"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={t("api_access.search_placeholder")}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
            </div>
            <p className={classes.helperText}>{t("api_access.toggle_off_helper")}</p>
            {apolloQueryError && <p className={classes.errorText}>{t("api_access.load_error")}</p>}
            <MainTable
                columns={columns}
                rows={!loading && !apolloQueryError ? filteredCorporations : []}
                loading={loading}
                tableCellCustomStyle={classes.tableRow}
                pagination
            />
            <ApiAccessKeysModal
                corporation={selectedCorporation}
                onClose={() => {
                    setSelectedCorporation(undefined);
                    refetch();
                }}
            />
        </div>
    );
};
